/**
 * ChatMessageConfirmation - Inline Confirmation for Destructive AI Operations
 *
 * sl-canvas2-8.31: Inline confirmation in chat flow (not modal!)
 *
 * Renders as a chat message with Approve/Reject buttons.
 * When user clicks, calls backend /respond endpoint directly.
 */
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';

import { confirmationCompletedEventBus } from '../../../../modules/cloud/services/eventsource';
import {
  aiChatBodyTypography,
  aiChatControlTypography,
  aiChatMetaTypography,
} from '../../styles/ai-chat-typography';
import { type LocalizedCopy, localizedCopy } from '../../utils/localized-copy';

export interface ConfirmationMessage {
  type: 'confirmation';
  id: string;
  toolName: string;
  displayName: string;
  description: string;
  copy?: LocalizedCopy;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  settingsHint?: string | null;
  metadata?: Record<string, unknown>;
  /** Set when user has responded */
  responded?: boolean;
  /** Set to true if approved, false if rejected */
  approved?: boolean;
  /** Tool result after execution */
  toolResult?: {
    success: boolean;
    data?: unknown;
    error?: string;
    hint?: string;
  };
}

type ConfirmationProfile = 'confirm_each' | 'policy' | 'autonomous';

export class ChatMessageConfirmation extends WithDisposable(ShadowlessElement) {
  /**
   * Theme-aware styles using AFFiNE CSS variables
   * Works with all themes: dark, light, blue-white, etc.
   */
  static override styles = css`
    chat-message-confirmation {
      display: block;
      margin: 0;
    }

    .confirmation-card {
      background: var(--streamlain-background-secondary-color);
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      padding: 10px;
      max-width: 100%;
      box-sizing: border-box;
    }

    .confirmation-description {
      ${aiChatBodyTypography}
      color: var(--streamlain-text-primary-color);
      margin-bottom: 10px;
      overflow-wrap: anywhere;
    }

    .confirmation-card.readonly .confirmation-description,
    .confirmation-card.approved .confirmation-description,
    .confirmation-card.rejected .confirmation-description {
      margin-bottom: 0;
    }

    .confirmation-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      align-items: center;
    }

    .confirmation-btn {
      ${aiChatControlTypography}
      min-height: 30px;
      padding: 5px 12px;
      border-radius: 6px;
      cursor: pointer;
      transition:
        background 0.15s ease,
        opacity 0.15s ease;
    }

    .confirmation-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-reject {
      background: var(--streamlain-background-tertiary-color);
      color: var(--streamlain-text-primary-color);
      border: 1px solid var(--streamlain-border-color);
    }

    .btn-reject:hover:not(:disabled) {
      background: var(--streamlain-hover-color);
    }

    .btn-approve {
      background: var(--streamlain-primary-color);
      color: var(--streamlain-pure-white);
    }

    .btn-approve:hover:not(:disabled) {
      opacity: 0.9;
    }

    /* Responded states - theme-aware */
    .confirmation-card.approved {
      /* Use a subtle tint of the background with success border */
      background: var(--streamlain-background-secondary-color);
      border-color: var(--streamlain-processing-color);
    }

    .confirmation-card.rejected {
      background: var(--streamlain-background-tertiary-color);
      border-color: var(--streamlain-border-color);
      opacity: 0.7;
    }

    .confirmation-status {
      ${aiChatMetaTypography}
      margin-top: 8px;
      color: var(--streamlain-text-secondary-color);
    }

    .confirmation-profile {
      ${aiChatMetaTypography}
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 8px;
      margin: 0 0 10px;
      color: var(--streamlain-text-secondary-color);
    }

    .confirmation-profile select {
      min-width: 0;
      max-width: 160px;
      height: 28px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 6px;
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font: inherit;
      padding: 0 24px 0 8px;
    }

    .confirmation-status.success {
      color: var(--streamlain-processing-color);
    }

    .confirmation-status.error {
      color: var(--streamlain-error-color);
    }
  `;

  @property({ attribute: false })
  accessor item!: ConfirmationMessage;

  @property({ attribute: false })
  accessor sessionId: string | undefined;

  @property({ attribute: false })
  accessor onRespond:
    | ((
        approved: boolean
      ) =>
        | void
        | ConfirmationMessage['toolResult']
        | Promise<void | ConfirmationMessage['toolResult']>)
    | undefined;

  @property({ attribute: false })
  accessor confirmationProfile: ConfirmationProfile = 'policy';

  @property({ type: Boolean })
  accessor showConfirmationProfile = false;

  @property({ attribute: false })
  accessor onConfirmationProfileChange:
    | ((profile: ConfirmationProfile) => void)
    | undefined;

  @property({ type: Boolean })
  accessor readOnly = false;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @state()
  accessor isSubmitting = false;

  @state()
  accessor responded = false;

  @state()
  accessor approved: boolean | undefined;

  @state()
  accessor toolResult: ConfirmationMessage['toolResult'] | undefined;

  @state()
  accessor error: string | undefined;

  override connectedCallback() {
    super.connectedCallback();
    // Initialize state from item
    this.responded = this.item.responded ?? false;
    this.approved = this.item.approved;
    this.toolResult = this.item.toolResult;
  }

  private async handleResponse(approve: boolean) {
    if (this.isSubmitting || this.responded) return;

    this.isSubmitting = true;
    this.error = undefined;

    try {
      if (this.onRespond) {
        const toolResult = await this.onRespond(approve);
        this.responded = true;
        this.approved = approve;
        this.toolResult =
          toolResult ??
          (approve
            ? { success: true }
            : { success: false, error: 'Cancelled' });
        this.dispatchEvent(
          new CustomEvent('confirmation-responded', {
            bubbles: true,
            composed: true,
            detail: {
              approved: approve,
              toolResult: this.toolResult,
            },
          })
        );
        return;
      }

      const response = await workspaceApiFetch(
        `/api/ai/confirmations/${this.item.id}/respond`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            approved: approve,
            reason: approve ? undefined : 'User rejected',
          }),
        },
        {
          workspaceId: this.workspaceId,
          category: 'foreground-action',
          mode: 'foreground',
          priority: 'foreground',
        }
      );

      if (response.status === 404) {
        // Confirmation expired (e.g. backend restart cleared Redis)
        this.responded = true;
        this.approved = false;
        this.error = undefined;
        this.toolResult = { success: false, error: 'Confirmation expired' };
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to respond to confirmation');
      }

      const data = await response.json();

      this.responded = true;
      this.approved = approve;
      this.toolResult = data.toolResult;
      this.dispatchEvent(
        new CustomEvent('confirmation-responded', {
          bubbles: true,
          composed: true,
          detail: {
            approved: approve,
            toolResult: data.toolResult,
          },
        })
      );

      // Emit event for auto-continue
      if (this.sessionId) {
        confirmationCompletedEventBus.emit({
          sessionId: this.sessionId,
          success: approve && data.toolResult?.success,
          toolResult: data.toolResult,
        });
      }

      // sl-718z: Notify workflow viewer of updated data after successful tool execution
      if (
        approve &&
        data.toolResult?.success &&
        data.toolResult?.data?.updatedWorkflow
      ) {
        // Derive changed node names from tool metadata + result
        const changedNodeNames: string[] = [];
        const meta = this.item.metadata;
        if (meta?.nodeName) changedNodeNames.push(meta.nodeName as string);
        const resultData = data.toolResult.data as Record<string, unknown>;
        if (resultData.insertedNodeName)
          changedNodeNames.push(resultData.insertedNodeName as string);

        window.dispatchEvent(
          new CustomEvent('streamlain:workflow-updated', {
            detail: {
              updatedWorkflow: data.toolResult.data.updatedWorkflow,
              changedNodeNames,
            },
          })
        );
      }
    } catch (err) {
      console.error('[ChatMessageConfirmation] Error:', err);
      this.error = 'Failed to process. Please try again.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private renderStatus() {
    if (!this.responded) return null;

    if (this.approved) {
      if (this.toolResult?.success) {
        return html`<div class="confirmation-status success">
          ✓ ${I18n.t('com.streamlain.ai.confirmation.status.success')}
        </div>`;
      } else if (this.toolResult?.error) {
        return html`<div class="confirmation-status error">
          ✗ ${this.toolResult.error}
        </div>`;
      }
      return html`<div class="confirmation-status success">
        ✓ ${I18n.t('com.streamlain.ai.confirmation.status.executing')}
      </div>`;
    } else {
      return html`<div class="confirmation-status">
        ✗ ${I18n.t('com.streamlain.ai.confirmation.status.cancelled')}
      </div>`;
    }
  }

  protected override render() {
    const description = localizedCopy(this.item.copy, this.item.description);
    const cardClasses = [
      'confirmation-card',
      this.readOnly ? 'readonly' : '',
      this.responded && this.approved ? 'approved' : '',
      this.responded && !this.approved ? 'rejected' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div class=${cardClasses}>
        <div class="confirmation-description">${description}</div>

        ${this.showConfirmationProfile && !this.readOnly && !this.responded
          ? html`<label class="confirmation-profile">
              <span
                >${I18n.t('com.streamlain.ai.confirmation.profile.label')}</span
              >
              <select
                .value=${this.confirmationProfile}
                @change=${(event: Event) => {
                  const value = (event.currentTarget as HTMLSelectElement)
                    .value as ConfirmationProfile;
                  this.onConfirmationProfileChange?.(value);
                }}
              >
                <option value="confirm_each">
                  ${I18n.t(
                    'com.streamlain.ai.confirmation.profile.confirm-each'
                  )}
                </option>
                <option value="policy">
                  ${I18n.t('com.streamlain.ai.confirmation.profile.policy')}
                </option>
                <option value="autonomous">
                  ${I18n.t('com.streamlain.ai.confirmation.profile.autonomous')}
                </option>
              </select>
            </label>`
          : null}
        ${this.error
          ? html`<div class="confirmation-status error">${this.error}</div>`
          : null}
        ${this.readOnly
          ? null
          : this.responded
            ? this.renderStatus()
            : html`
                <div class="confirmation-actions">
                  <button
                    class="confirmation-btn btn-reject"
                    @click=${() => this.handleResponse(false)}
                    ?disabled=${this.isSubmitting}
                  >
                    ${this.item.cancelLabel ??
                    I18n.t('com.streamlain.ai.confirmation.cancel')}
                  </button>
                  <button
                    class="confirmation-btn btn-approve"
                    @click=${() => this.handleResponse(true)}
                    ?disabled=${this.isSubmitting}
                  >
                    ${this.isSubmitting
                      ? I18n.t('com.streamlain.ai.confirmation.processing')
                      : (this.item.confirmLabel ??
                        I18n.t('com.streamlain.ai.confirmation.confirm'))}
                  </button>
                </div>
              `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-message-confirmation': ChatMessageConfirmation;
  }
}
